import { getPayerDisplayName, PAYERS, STATE_CODE_TO_NAME, LOB_VALUE_TO_NAME, US_STATES } from "../../config";
import { Payer } from "../../services";

// Helper to check if a value represents National
export const isNationalValue = (value: string): boolean => 
    value === 'ALL' || value?.toLowerCase() === 'national'

export class PreChatsHelper {
    static extractAvailableStates(payersData: Payer[]) {
        try{
            if (!payersData || payersData.length === 0) {
                return [];
            }
            // Convert state codes/names to state names and filter to only include valid states
            // Include "National" even if it's not in US_STATES
            return payersData
                .map(state => {
                    // Use state.name if it's already a state name, otherwise convert code to name
                    const stateName = US_STATES.includes(state.name) || isNationalValue(state.name)
                        ? state.name 
                        : STATE_CODE_TO_NAME[state.code] || state.name;
    
                        return {
                        value: state.code,
                        label: isNationalValue(stateName) ? 'National' : stateName
                    };
                })
                .filter(state => {
                    return state.value && (US_STATES.includes(state.label) || state.label === 'National');
                })
                // The API can still return more than one bucket that renders as
                // the same label -- "ALL" and "NATIONAL" both mean National --
                // which showed up as a duplicated National entry (REV-2393).
                .filter((state, index, all) =>
                    all.findIndex(other => other.label === state.label) === index
                )
                .sort((a, b) => {
                    // Put National (value "ALL" or "NATIONAL") first
                    if (isNationalValue(a.value)) return -1;
                    if (isNationalValue(b.value)) return 1;
                    // Sort rest alphabetically
                    return a.label.localeCompare(b.label);
                });
        }
        catch {
            return []
        }
    }

    static extractPayerOptions(state: string, selectedState: Payer | undefined, payersData: Payer[]) {
        try{
            if (!state || !selectedState) {
                // No state selected: extract all unique payers from all states
                const allPayersMap = new Map<string, { name: string; lines_of_business: string[] }>();
                
                payersData.forEach(state => {
                    if (state.payers && state.payers.length > 0) {
                        state.payers.forEach(payer => {
                            // If payer already exists, merge lines of business
                            if (allPayersMap.has(payer.name)) {
                                const existingPayer = allPayersMap.get(payer.name)!;
                                // Merge unique lines of business
                                const mergedLob = Array.from(new Set([
                                    ...existingPayer.lines_of_business,
                                    ...payer.lines_of_business
                                ]));
                                allPayersMap.set(payer.name, {
                                    name: payer.name,
                                    lines_of_business: mergedLob
                                });
                            } else {
                                allPayersMap.set(payer.name, {
                                    name: payer.name,
                                    lines_of_business: [...payer.lines_of_business]
                                });
                            }
                        });
                    }
                });
                
                // Convert to options array and sort by display name
                return Array.from(allPayersMap.values())
                    .map(payer => ({
                        label: getPayerDisplayName(payer.name),
                        value: payer.name
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
            }
            
            // State is selected: show payers from selected state only
            if (!selectedState || !selectedState.payers || selectedState.payers.length === 0) {
                return [];
            }
            
            return selectedState.payers.map(payer => ({
                label: getPayerDisplayName(payer.name),  // Map using PAYERS
                value: payer.name  // Store API name as value for form submission
            }));
        }
        catch {
            return []
        }
    }

    // Hardcoded states with all payers and LOBs, used when getPayers() fails or errors.
    static buildFallbackPayers(): Payer[] {
        return Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => ({
            code,
            name,
            payers: Object.keys(PAYERS).map(apiName => ({
                name: apiName,
                lines_of_business: Object.keys(LOB_VALUE_TO_NAME)
            }))
        }));
    }
}